import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { sendTaskToLambda, fetchTasksFromLambda, updateTaskInLambda, deleteTaskFromLambda, fetchWorkspacesFromLambda, updateWorkspaceInLambda, logMessage, deleteWorkspaceFromLambda, fetchUserPermissions, refreshData } from '../utils/lambdaApi';
import { shouldTaskBeArchived, filterArchivedTasks, getArchivingStats, generateLocalTimestamp } from '../utils/taskUtils';
import { getDefaultBoardPermissions } from '../utils/permissionKeys.js';

// Crear un logger local para reemplazar las referencias a logger
const logger = {
  info: (message, data) => {},
  error: (message, error) => console.error(message, error),
  important: (message, data) => {}
};

// Datos iniciales para demostración por espacio de trabajo
const initialWorkspaces = {
  legal: {
    tasks: [],
    availableTags: ["documento", "legal", "urgente"],
    activeFilter: "",
    activeAssigneeFilter: "",
    activeTitleFilter: ""
  },
  marketing: {
    tasks: [],
    availableTags: ["redes sociales", "diseño", "campañas"],
    activeFilter: "",
    activeAssigneeFilter: "",
    activeTitleFilter: ""
  },
  hr: {
    tasks: [],
    availableTags: ["rrhh", "reunión", "evaluación", "contratación"],
    activeFilter: "",
    activeAssigneeFilter: "",
    activeTitleFilter: ""
  },
  it: {
    tasks: [],
    availableTags: ["mantenimiento", "urgente", "seguridad", "documento", "datos"],
    activeFilter: "",
    activeAssigneeFilter: "",
    activeTitleFilter: ""
  }
};

// Información metadata de workspaces
const initialWorkspaceData = [
  { id: 'legal', name: 'Legal', icon: '' },
  { id: 'marketing', name: 'Marketing', icon: '' },
  { id: 'hr', name: 'HR', icon: '' },
  { id: 'it', name: 'IT', icon: '' },
];

// Crear el contexto
const KanbanContext = createContext();

// Hook personalizado para acceder al contexto
export function useKanban() {
  const context = useContext(KanbanContext);
  
  // Si el contexto es undefined, estamos fuera del KanbanProvider
  // En ese caso, devolver un objeto vacío en lugar de provocar un error
  if (context === undefined) {
    // Devolver un objeto vacío que coincida con la estructura esperada
    return {
      workspaceData: [],
      activeWorkspace: '',
      userAccessibleWorkspaces: [],
      workspacesLoading: false
    };
  }
  
  return context;
}

// Proveedor del contexto
export function KanbanProvider({ children }) {
  const { currentUser, userData } = useAuth();
  // Intentar cargar datos del localStorage o usar datos iniciales
  const [workspaces, setWorkspaces] = useState(() => {
    if (typeof window !== "undefined") {
      const savedWorkspaces = localStorage.getItem("kanbanWorkspaces");
      return savedWorkspaces ? JSON.parse(savedWorkspaces) : initialWorkspaces;
    }
    return initialWorkspaces;
  });

  const [workspaceData, setWorkspaceData] = useState(() => {
    if (typeof window !== "undefined") {
      const savedWorkspaceData = localStorage.getItem("workspaceData");
      return savedWorkspaceData ? JSON.parse(savedWorkspaceData) : initialWorkspaceData;
    }
    return initialWorkspaceData;
  });

  const [activeWorkspace, setActiveWorkspaceState] = useState(() => {
    if (typeof window !== "undefined") {
      const savedWorkspace = localStorage.getItem("activeWorkspace");
      return savedWorkspace || "legal";
    }
    return "legal";
  });
  
  // Custom wrapper around setActiveWorkspace that invalidates cache
  const setActiveWorkspace = (workspaceId) => {
    // Only take action if actually changing workspace
    if (workspaceId !== activeWorkspace) {
      // Solo intentar cargar datos si hay un usuario autenticado
      if (currentUser) {
        // Force cache invalidation when switching workspaces
        // This will make fetchTasksFromLambda get fresh data
        fetchTasksFromLambda(workspaceId, true)
          .then(() => {

          })
          .catch(error => {
            console.error(`Error refreshing data for workspace ${workspaceId}:`, error);
          });
      }
      
      // Update state
      setActiveWorkspaceState(workspaceId);
    }
  };

  const [userAccessibleWorkspaces, setUserAccessibleWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState({ admin: false, additionalCodes: [], sendTasks: false, perBoardPermissions: {} });

  // Function to load user permissions
  const loadUserPermissions = useCallback(async () => {
    if (!currentUser || !currentUser.email) {
      console.error('No se puede cargar permisos: usuario o email no disponible');
      const defaults = { admin: false, additionalCodes: [], sendTasks: false, perBoardPermissions: {} };
      setUserPermissions(defaults);
      return defaults;
    }
    
    try {
      const permissions = await fetchUserPermissions(currentUser.email);
      setUserPermissions(permissions);
      logMessage('Permisos de usuario cargados:', permissions);
      return permissions;
    } catch (error) {
      console.error('Error al cargar permisos de usuario:', error);
      const defaults = { admin: false, additionalCodes: [], sendTasks: false, perBoardPermissions: {} };
      setUserPermissions(defaults);
      return defaults;
    }
  }, [currentUser]);

  // Function to load workspaces from Lambda
  const loadWorkspaces = useCallback(async (departmentCode, forceRefresh = false) => {
    setWorkspacesLoading(true);
    
    try {
      // Verificar que tenemos un código de departamento válido
      if (!departmentCode && !userPermissions.admin && userPermissions.additionalCodes.length === 0) {
        console.error('No se puede cargar workspaces: no hay código de departamento ni códigos adicionales');
        setUserAccessibleWorkspaces([]);
        setWorkspacesLoading(false);
        return;
      }

      // Load user permissions first
      const permissions = forceRefresh ? 
        await loadUserPermissions() : 
        userPermissions;
      
      const isAdmin = permissions.admin;
      const additionalCodes = permissions.additionalCodes;
      
      logMessage('Cargando workspaces, admin:', isAdmin, 'códigos adicionales:', additionalCodes);
      
      let workspacesFromLambda = [];
      
      // In admin mode, we can use an empty departmentCode
      if (isAdmin) {
        // Admin can see all workspaces
        workspacesFromLambda = await fetchWorkspacesFromLambda('', true, forceRefresh);
      } else if (departmentCode || additionalCodes.length > 0) {
        // Regular user with department code or additional codes
        const departmentWorkspaces = departmentCode ? 
          await fetchWorkspacesFromLambda(departmentCode, false, forceRefresh) : 
          [];
        
        // If user has additional codes, fetch workspaces for each code
        const additionalWorkspaces = [];
        for (const code of additionalCodes) {
          const codeWorkspaces = await fetchWorkspacesFromLambda(code, false, forceRefresh);
          additionalWorkspaces.push(...codeWorkspaces);
        }
        
        // Combine workspaces and remove duplicates based on uuid
        const allWorkspaces = [...departmentWorkspaces, ...additionalWorkspaces];
        const uniqueUuids = new Set();
        workspacesFromLambda = allWorkspaces.filter(ws => {
          if (uniqueUuids.has(ws.uuid)) return false;
          uniqueUuids.add(ws.uuid);
          return true;
        });
        
        // Si el usuario tiene permisos sendTasks, también buscar workspaces donde tenga tareas creadas
        if (permissions.sendTasks && currentUser?.email) {
          try {
            const userTaskWorkspaces = await findWorkspacesWithUserTasks(currentUser.email);
            
            // Agregar workspaces donde el usuario tiene tareas, evitando duplicados
            for (const taskWs of userTaskWorkspaces) {
              if (!uniqueUuids.has(taskWs.uuid)) {
                workspacesFromLambda.push(taskWs);
                uniqueUuids.add(taskWs.uuid);
              }
            }
            
            logMessage(`Encontrados ${userTaskWorkspaces.length} workspaces con tareas del usuario`);
          } catch (error) {
            console.error('Error al buscar workspaces con tareas del usuario:', error);
          }
        }
      } else {
        console.error('No se puede cargar workspaces: no hay código de departamento ni códigos adicionales');
        setUserAccessibleWorkspaces([]);
        setWorkspacesLoading(false);
        return;
      }
      
      if (workspacesFromLambda.length > 0) {
        logMessage('Workspaces recibidos del Lambda:', workspacesFromLambda);
        
        // Convertir los workspaces del Lambda al formato que espera la aplicación
        const newWorkspaces = {};
        const newWorkspaceData = [];
        
        // Procesar cada workspace del Lambda
        workspacesFromLambda.forEach(ws => {
          const workspaceId = ws.workspace.toLowerCase().replace(/\s+/g, '-');
          

          
          // Si este workspace ya existe en el estado actual, mantener sus tareas y configuración
          if (workspaces[workspaceId]) {
            newWorkspaces[workspaceId] = {
              ...workspaces[workspaceId],
              // Cargar etiquetas desde DynamoDB si están disponibles
              availableTags: Array.isArray(ws.availableTags) ? ws.availableTags : (workspaces[workspaceId].availableTags || [])
            };
          } else {
            // Si es un nuevo workspace, crear su estructura básica
            newWorkspaces[workspaceId] = {
              tasks: [],
              availableTags: ws.availableTags || [], // Cargar etiquetas desde DynamoDB si están disponibles
              activeFilter: "",
              activeAssigneeFilter: "",
              activeTitleFilter: ""
            };
          }
          
          // Añadir al workspaceData para la interfaz
          newWorkspaceData.push({
            id: workspaceId,
            name: ws.workspace,
            icon: getDefaultIcon(ws.workspace),
            uuid: ws.uuid,  // Añadir el UUID de Lambda
            code: ws.code,  // ¡IMPORTANTE! Incluir el código del workspace
            isUserTasksOnly: ws.isUserTasksOnly || false, // Marcar si es solo para tareas del usuario
            userTaskCount: ws.userTaskCount || 0, // Número de tareas del usuario
            availableTags: ws.availableTags || [], // Guardar las etiquetas de DynamoDB para comparación
            permisos: ws.permisos ?? ws.Permisos ?? null // Restricciones del tablero (backend puede enviar permisos o Permisos)
          });
        });
        
        // Actualizar el estado de los workspaces y su metadata
        if (Object.keys(newWorkspaces).length > 0) {
          setWorkspaces(prevWorkspaces => ({
            ...prevWorkspaces,
            ...newWorkspaces
          }));
          
          setWorkspaceData(newWorkspaceData);
          setUserAccessibleWorkspaces(newWorkspaceData);
          
          // Comparar y sincronizar etiquetas con DynamoDB para todos los workspaces después de cargarlos
          setTimeout(async () => {
            for (const ws of newWorkspaceData) {
              await compareAndSyncTagsWithDynamoDB(ws.id);
            }
          }, 100);
          
          // Si no hay workspace activo o el actual no es accesible, seleccionar el primero
          if (
            newWorkspaceData.length > 0 && 
            (!activeWorkspace || !newWorkspaceData.some(ws => ws.id === activeWorkspace))
          ) {
            setActiveWorkspace(newWorkspaceData[0].id);
          }
        } else {
          setUserAccessibleWorkspaces([]);
        }
      } else {
        setUserAccessibleWorkspaces([]);
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
      setUserAccessibleWorkspaces([]);
    } finally {
      setWorkspacesLoading(false);
    }
  }, [currentUser]);

  // Guardar en localStorage cuando cambian los datos
  useEffect(() => {
    localStorage.setItem("kanbanWorkspaces", JSON.stringify(workspaces));
  }, [workspaces]);

  useEffect(() => {
    localStorage.setItem("workspaceData", JSON.stringify(workspaceData));
  }, [workspaceData]);

  // Ejecutar migración de etiquetas cuando se carguen los workspaces
  useEffect(() => {
    if (workspaceData.length > 0 && Object.keys(workspaces).length > 0 && !workspacesLoading) {
      // Ejecutar migración una sola vez cuando los datos estén cargados
      const migrationKey = 'tagsMigrationCompleted';
      const migrationCompleted = localStorage.getItem(migrationKey);
      
      if (!migrationCompleted) {
        // eslint-disable-next-line no-use-before-define
        migrateTagsToDatabase().then(() => {
          localStorage.setItem(migrationKey, 'true');
        }).catch(error => {
          console.error('Error en la migración de etiquetas:', error);
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceData, workspaces, workspacesLoading]);

  // Ejecutar proceso de archivado automático cada 30 minutos
  useEffect(() => {
    const runAutoArchiving = async () => {
      if (!activeWorkspace || !currentUser) return;
      
      try {
        const currentTasks = workspaces[activeWorkspace]?.tasks || [];
        const tasksToArchive = [];
        
        // Verificar qué tareas necesitan ser archivadas
        currentTasks.forEach(task => {
          if (shouldTaskBeArchived(task)) {
            tasksToArchive.push(task);
          }
        });
        
        // Si hay tareas para archivar, procesarlas
        if (tasksToArchive.length > 0) {
    
          
          // Actualizar cada tarea en el backend
          for (const task of tasksToArchive) {
            try {
              await updateTaskInLambda(task.id, { ArchivedDate: true });

            } catch (error) {
              console.error(`Error al archivar automáticamente la tarea ${task.id}:`, error);
            }
          }
          
          // Recargar las tareas para reflejar los cambios
          // eslint-disable-next-line no-use-before-define
          await reloadTasksFromServer(activeWorkspace);
        }
      } catch (error) {
        console.error('Error en el proceso automático de archivado:', error);
      }
    };
    
    // Ejecutar inmediatamente al cargar
    runAutoArchiving();
    
    // Configurar intervalo para ejecutar cada 30 minutos (1800000 ms)
    const archivingInterval = setInterval(runAutoArchiving, 1800000);
    
    // Limpiar el intervalo al desmontar el componente
    return () => clearInterval(archivingInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace, currentUser, workspaces]);

  useEffect(() => {
    localStorage.setItem("activeWorkspace", activeWorkspace);
  }, [activeWorkspace]);

  // Efecto para cargar permisos de usuario y workspaces cuando el usuario cambia
  useEffect(() => {
    if (currentUser && userData) {
      // Verificar si userData.body existe, ya que el departamento puede estar ahí
      // eslint-disable-next-line no-unused-vars
      const userDataContent = userData.body || userData;
      
      // Determinar la estructura correcta de los datos
      if (userData.body) {
        // Si body es string, intentar parsearlo
        if (typeof userData.body === 'string') {
          try {
            // eslint-disable-next-line no-unused-vars
            const parsedBody = JSON.parse(userData.body);
          } catch (e) {
            console.error('Error al parsear userData.body:', e);
          }
        }
      }
      
      // Intentar extraer el código de departamento de múltiples ubicaciones posibles
      let departmentValue = null;
      
      // Opción 1: directamente en userData.department
      if (userData.department) {
        departmentValue = userData.department;
      } 
      // Opción 2: en userData.body si es un objeto
      else if (userData.body && typeof userData.body === 'object' && userData.body.department) {
        departmentValue = userData.body.department;
      } 
      // Opción 3: en userData.body si es un string JSON
      else if (userData.body && typeof userData.body === 'string') {
        try {
          const parsedBody = JSON.parse(userData.body);
          if (parsedBody.department) {
            departmentValue = parsedBody.department;
          }
        } catch (e) {
          console.error('Error al parsear userData.body para encontrar department:', e);
        }
      }
      
      // Si no encontramos el departamento, buscar en toda la estructura
      if (!departmentValue) {
        // Función para buscar recursivamente en un objeto
        const findDepartment = (obj, path = '') => {
          if (!obj || typeof obj !== 'object') return null;
          
          for (const key in obj) {
            const currentPath = path ? `${path}.${key}` : key;
            
            if (key === 'department') {
              return obj[key];
            }
            
            if (typeof obj[key] === 'object' && obj[key] !== null) {
              const found = findDepartment(obj[key], currentPath);
              if (found) return found;
            }
          }
          
          return null;
        };
        
        departmentValue = findDepartment(userData);
        if (!departmentValue) {
          console.error('No se pudo encontrar el campo department en ninguna parte de userData');
        }
      }
      
      // Ahora extraer el código con la expresión regular
      let userDepartmentCode = undefined;
      if (departmentValue) {
        const departmentCodeMatch = departmentValue.match(/^(\d+)/);
        userDepartmentCode = departmentCodeMatch ? departmentCodeMatch[1] : departmentValue;
      } else {
        console.error('No se pudo extraer código de departamento: campo department no encontrado');
      }
      
      // Cargar permisos y luego los workspaces
      const loadData = async () => {
        try {
          setWorkspacesLoading(true);
          
          // Primero cargar permisos de usuario
          await loadUserPermissions();
          
          // Luego cargar workspaces solo si tenemos un código de departamento
          if (userDepartmentCode) {
            await loadWorkspaces(userDepartmentCode, true);
          } else {
            console.error('Código de departamento no disponible en los datos del usuario');
            setUserAccessibleWorkspaces([]);
          }
          
          // Cuando termine la carga, activar un indicador para recargar tareas
          sessionStorage.setItem('refreshTasksOnLogin', 'true');
        } catch (error) {
          console.error('Error al cargar datos iniciales:', error);
        } finally {
          setWorkspacesLoading(false);
        }
      };
      
      loadData();
    } else {
      setUserAccessibleWorkspaces([]);
    }
  }, [currentUser, userData, loadUserPermissions, loadWorkspaces]);

  // Efecto para cargar las tareas desde Lambda cuando cambia el workspace activo
  useEffect(() => {
    if (!activeWorkspace || !currentUser) return;
    
    const loadTasksFromLambda = async () => {
      try {
        setWorkspacesLoading(true);
        logMessage(`Cargando tareas para workspace '${activeWorkspace}'...`);
        
        const tasksFromLambda = await fetchTasksFromLambda(activeWorkspace);
        
        logMessage(`Respuesta de Lambda para '${activeWorkspace}':`, tasksFromLambda);
        
        if (Array.isArray(tasksFromLambda)) {
          // Verificar si este workspace es solo para tareas del usuario
          const currentWorkspaceData = userAccessibleWorkspaces.find(ws => ws.id === activeWorkspace);
          let filteredTasks = tasksFromLambda;
          
          // Si es un workspace solo para tareas del usuario, filtrar las tareas
          if (currentWorkspaceData?.isUserTasksOnly && currentUser?.email) {
            filteredTasks = tasksFromLambda.filter(task => 
              task.createdBy === currentUser.email
            );
            
            logMessage(`Filtradas ${filteredTasks.length} tareas del usuario de ${tasksFromLambda.length} tareas totales`);
          }
          
          // Procesar tareas para archivado automático
          const tasksToArchive = [];
          const processedTasks = filteredTasks.map(task => {
            if (shouldTaskBeArchived(task)) {
              tasksToArchive.push(task);
              return {
                ...task,
                ArchivedDate: true
              };
            }
            return task;
          });
          
          // Si hay tareas que necesitan ser archivadas, actualizarlas en el backend
          if (tasksToArchive.length > 0) {
            logMessage(`Archivando automáticamente ${tasksToArchive.length} tareas que han pasado 72 horas desde su fecha de vencimiento`);
            
            // Actualizar cada tarea archivada en el backend
            for (const task of tasksToArchive) {
              try {
                await updateTaskInLambda(task.id, { ArchivedDate: true });
                logMessage(`Tarea ${task.id} archivada automáticamente`);
              } catch (error) {
                console.error(`Error al archivar automáticamente la tarea ${task.id}:`, error);
              }
            }
          }
          
          // Filtrar las tareas archivadas para no mostrarlas en el frontend
          const visibleTasks = filterArchivedTasks(processedTasks);
          
          // Actualizar las tareas para el workspace activo
          setWorkspaces(prevWorkspaces => {
            // Aseguramos que el workspace existe
            const updatedWorkspace = prevWorkspaces[activeWorkspace] 
              ? { ...prevWorkspaces[activeWorkspace], tasks: visibleTasks }
              : { tasks: visibleTasks, availableTags: [], activeFilter: "", activeAssigneeFilter: "", activeTitleFilter: "" };
            
            return {
              ...prevWorkspaces,
              [activeWorkspace]: updatedWorkspace
            };
          });
          
          // Comparar y sincronizar etiquetas con DynamoDB después de cargar las tareas
          setTimeout(async () => {
            // eslint-disable-next-line no-use-before-define
            await compareAndSyncTagsWithDynamoDB(activeWorkspace);
          }, 0);
          
          logMessage(`Se cargaron ${visibleTasks.length} tareas visibles para '${activeWorkspace}' (${tasksToArchive.length} archivadas automáticamente)`);
        } else {
          console.error("El formato de respuesta del Lambda no es un array:", tasksFromLambda);
        }
      } catch (error) {
        console.error(`Error al cargar tareas para ${activeWorkspace}:`, error);
        // En caso de error, mantener las tareas existentes
      } finally {
        setWorkspacesLoading(false);
      }
    };
    
    loadTasksFromLambda();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace, userAccessibleWorkspaces, currentUser]);

  // Funciones para manipular las tareas por espacio de trabajo
  const addTask = async (workspace, task) => {
    // Validar permisos solo si se está enviando a un tablero diferente al activo
    // Para creación local en el tablero actual, solo se necesita acceso al tablero
    const isSendingToAnotherBoard = workspace !== activeWorkspace;
    
    if (isSendingToAnotherBoard && !userPermissions.admin && !userPermissions.sendTasks) {
      console.error('El usuario no tiene permisos para enviar tareas a otros tableros');
      throw new Error('No tienes permisos para enviar tareas a otros tableros');
    }
    
    // Ya no generamos ID localmente, Lambda lo genera
    
    // Preparar tarea con workspace incluido
    const taskWithWorkspace = {
      ...task,
      workspace: workspace,
      comments: task.comments || []
    };
    
    // Intentar guardar en Lambda primero
    try {
      logMessage(`Enviando nueva tarea a Lambda para workspace '${workspace}'...`);
      const response = await sendTaskToLambda(taskWithWorkspace);
      
      // Si la tarea se guardó correctamente en DynamoDB...
      if (response) {
        logMessage('Tarea guardada correctamente en Lambda:', response);
        
        // Obtener ID generado por Lambda si está disponible
        let lambdaId = null;
        if (response.body) {
          try {
            const parsedBody = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
            lambdaId = parsedBody.uuid || parsedBody.id;
          } catch (error) {
            console.error('Error parsing Lambda response body:', error);
          }
        }
        
        // Usar el ID de Lambda o un generado localmente como fallback
        const finalTaskId = lambdaId || `local-${Date.now()}`;
        
        // Actualizar estado local con tarea actualizada
        const finalTask = {
          ...taskWithWorkspace,
          id: finalTaskId,
          uuid: finalTaskId, // Añadir uuid igual al id
          createdTimestamp: Date.now(), // Solo para mantener compatibilidad local
          updatedAt: new Date().toLocaleDateString()
        };
        
        // Update local state immediately for UI responsiveness
        setWorkspaces(prevWorkspaces => {
          const currentWorkspace = prevWorkspaces[workspace] || { tasks: [], availableTags: [], activeFilter: "", activeAssigneeFilter: "", activeTitleFilter: "" };
          
          // Añadir la tarea al array de tareas actual
          const updatedTasks = [...currentWorkspace.tasks, finalTask];
          
          // No es necesario ordenar aquí porque KanbanBoard ya se encarga de ordenar por fecha
          return {
            ...prevWorkspaces,
            [workspace]: {
              ...currentWorkspace,
              tasks: updatedTasks
            }
          };
        });
        
        // Comparar y sincronizar etiquetas con DynamoDB después de añadir la nueva tarea
        setTimeout(async () => {
          await compareAndSyncTagsWithDynamoDB(workspace);
        }, 0);
        
        // Then fetch the latest data from the server to ensure we're in sync
        fetchTasksFromLambda(workspace, true).then(tasksFromLambda => {
          if (Array.isArray(tasksFromLambda)) {
            setWorkspaces(prevWorkspaces => ({
              ...prevWorkspaces,
              [workspace]: {
                ...prevWorkspaces[workspace],
                tasks: tasksFromLambda
              }
            }));
          }
        }).catch(error => {
          console.error('Error refreshing tasks after adding new task:', error);
        });
        
        return finalTask;
      }
    } catch (error) {
      console.error('Error al guardar tarea en Lambda:', error);
    }
    
    return null;
  };

  // Get user permissions
  const getUserPermissions = () => {
    return userPermissions;
  };

  /**
   * Indica si el usuario tiene un permiso concreto en un tablero.
   * @param {string} permissionKey - Ej: 'moveBetweenStates', 'createTasks'
   * @param {string} boardCode - Código del tablero (workspace.code)
   * @returns {boolean}
   */
  const getPermissionForBoard = (permissionKey, boardCode) => {
    if (!permissionKey || !boardCode) return false;
    if (userPermissions.admin) return true;
    const perBoard = userPermissions.perBoardPermissions || {};
    const boardPerms = perBoard[boardCode];
    if (!boardPerms) return false;
    return !!boardPerms[permissionKey];
  };

  /**
   * Indica si el usuario puede realizar una acción en el tablero actual (o en el indicado).
   * Si el tablero tiene restricción para esa acción, solo puede si tiene el permiso en perBoardPermissions.
   * @param {string} permissionKey - Ej: 'moveBetweenStates', 'createTasks'
   * @param {string} [workspaceId] - id del workspace (por defecto activeWorkspace)
   * @returns {boolean}
   */
  const canUserDoOnBoard = (permissionKey, workspaceId = activeWorkspace) => {
    if (!permissionKey) return false;
    if (userPermissions.admin) return true;
    const workspace = workspaceData.find(ws => ws.id === workspaceId);
    if (!workspace || !workspace.code) return false;
    const restrictions = workspace.permisos != null
      ? (typeof workspace.permisos === 'string' ? (() => { try { return JSON.parse(workspace.permisos); } catch { return {}; } })() : workspace.permisos)
      : {};
    if (!restrictions[permissionKey]) return true;
    return getPermissionForBoard(permissionKey, workspace.code);
  };

  // Funciones para manipular las tareas por espacio de trabajo
  const updateTaskStatus = async (workspace, taskId, newStatus) => {
    // Find the task to update
    const task = workspaces[workspace]?.tasks.find(task => task.id === taskId);
    
    if (task) {
      // Si el estado no ha cambiado, no hacer nada
      if (task.status === newStatus) return;
      
      // Crear el timestamp para todas las actualizaciones
      const timestamp = generateLocalTimestamp(); // Usar timestamp local
      const userEmail = currentUser?.email || 'Usuario';

      // Crear el objeto de cambio de estado para el historial antiguo (compatibilidad)
      const statusChange = {
        timestamp: timestamp,
        oldStatus: task.status,
        newStatus: newStatus,
        updatedBy: userEmail
      };

      // Determinar el tipo de actividad según la transición de estados
      let activityType = "STATUS_CHANGE";
      
      // Si estamos pasando de inProgress a todo, es un descarte
      if (task.status === 'inProgress' && newStatus === 'todo') {
        activityType = "TASK_DISCARDED";
      }

      // Crear entrada para el historial de actividad unificado
      const activityEntry = {
        timestamp: timestamp,
        type: activityType,
        field: "status",
        oldValue: task.status,
        newValue: newStatus,
        updatedBy: userEmail
      };

      // Actualizar historiales
      // eslint-disable-next-line no-unused-vars
      const statusHistory = [...(task.statusHistory || []), statusChange];
      const activityHistory = [...(task.activityHistory || []), activityEntry];

      // Preparar objeto de actualizaciones
      const updates = {
        status: newStatus,
        activityHistory: activityHistory,
        updatedAt: timestamp
      };
      
      // Si el estado es "done" (completado), añadir la fecha de finalización
      if (newStatus === 'done') {
        // Usar generateLocalTimestamp para consistencia
        updates.completedDate = generateLocalTimestamp();
        
        // Ya no sobreescribimos la fecha de vencimiento
        // updates.endDate = currentDate; // <-- Eliminar esta línea
        
        // Ya no creamos una entrada adicional de DEADLINE_CHANGE, solo actualizamos los campos
        // para evitar registros redundantes cuando se completa una tarea
      }
      
      // Si estamos pasando de inProgress a todo, desasignar al usuario
      if (task.status === 'inProgress' && newStatus === 'todo' && task.assignee) {
        // Añadir evento de cambio de asignación al historial
        const assigneeActivityEntry = {
          timestamp: timestamp,
          type: "ASSIGNEE_CHANGE",
          field: "assignee",
          oldValue: task.assignee,
          newValue: null,
          updatedBy: userEmail
        };
        
        // Actualizar el historial
        updates.activityHistory = [...updates.activityHistory, assigneeActivityEntry];
        
        // Quitar asignación
        updates.assignee = null;
      }
      
      // Update local state first
      setWorkspaces(prevWorkspaces => ({
        ...prevWorkspaces,
        [workspace]: {
          ...prevWorkspaces[workspace],
          tasks: prevWorkspaces[workspace].tasks.map(task => 
            task.id === taskId ? { 
              ...task,
              ...updates
            } : task
          )
        }
      }));
      
      try {
        // Mostrar exactamente qué estamos enviando a Lambda

        
        // Call Lambda to update the task (usando taskId que ahora es el mismo que uuid)
        await updateTaskInLambda(taskId, updates);
      } catch (error) {
        console.error('Error updating task status in Lambda:', error);
      }
    }
  };

  // Actualizar una tarea existente en el workspace
  const updateTask = async (workspace, taskId, updatedData) => {
    try {
      logger.info(`Updating task ${taskId} in workspace ${workspace}`);
      
      // Verificar que la tarea existe
      const currentTasks = workspaces[workspace]?.tasks || [];
      const taskIndex = currentTasks.findIndex(t => t.id === taskId);
      
      if (taskIndex === -1) {
        console.error(`Task ${taskId} not found in workspace ${workspace}`);
        return;
      }
      
      const currentTask = currentTasks[taskIndex];
      
      // Eliminar campos redundantes del objeto updatedData
      if (updatedData.pdfs !== undefined) {
        delete updatedData.pdfs;
      }
      
      if (updatedData.enlaces !== undefined) {
        delete updatedData.enlaces;
      }
      
      // Crear timestamp para la actualización
      const timestamp = new Date().toISOString();
      
      // Manejar correctamente el historial de actividades
      let mergedActivityHistory = [...(currentTask.activityHistory || [])];
      
      // Si hay nuevas entradas de actividad, agregarlas al historial existente
      if (updatedData.activityHistory && Array.isArray(updatedData.activityHistory)) {
        // Determinar qué entradas son nuevas (no existen en el historial actual)
        const existingTimestamps = new Set(mergedActivityHistory.map(entry => entry.timestamp));
        const newEntries = updatedData.activityHistory.filter(entry => !existingTimestamps.has(entry.timestamp));
        
        // Registrar las nuevas entradas para depuración
        if (newEntries.length > 0) {

        }
        
        // Agregar las nuevas entradas al historial combinado
        mergedActivityHistory = [...mergedActivityHistory, ...newEntries];
        
        // Ordenar el historial por timestamp (de más reciente a más antiguo)
        mergedActivityHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      }
      
      // Preparar la actualización
      const mergedTask = {
        ...updatedData,
        updatedAt: timestamp,
        activityHistory: mergedActivityHistory // Usar el historial combinado
      };
      
      // Si sólo se está actualizando el historial de actividad, solo mantener ese campo y updatedAt
      if (Object.keys(updatedData).length === 1 && updatedData.activityHistory) {
        // Si solo se actualizó el historial, crear una versión más simple del objeto de actualización
        const simplifiedUpdate = {
          activityHistory: mergedActivityHistory,
          updatedAt: timestamp
        };
        
        // Update local state first for responsiveness
        setWorkspaces(prevWorkspaces => ({
          ...prevWorkspaces,
          [workspace]: {
            ...prevWorkspaces[workspace],
            tasks: prevWorkspaces[workspace].tasks.map(task => 
              task.id === taskId ? { 
                ...task, 
                ...simplifiedUpdate 
              } : task
            )
          }
        }));
        
        try {
          // Enviar actualización a Lambda
          await updateTaskInLambda(taskId, simplifiedUpdate);
          
          // NO recargar automáticamente para evitar cerrar modales

        } catch (error) {
          console.error('Error updating task activity history in Lambda:', error);
          throw error;
        }
      } else {
        // Update local state first for responsiveness
        setWorkspaces(prevWorkspaces => ({
          ...prevWorkspaces,
          [workspace]: {
            ...prevWorkspaces[workspace],
            tasks: prevWorkspaces[workspace].tasks.map(task => 
              task.id === taskId ? { 
                ...task, 
                ...mergedTask 
              } : task
            )
          }
        }));
        
        try {
          // Enviar actualización a Lambda
          await updateTaskInLambda(taskId, mergedTask);
          
          // Comparar y sincronizar etiquetas con DynamoDB después de actualizar
          setTimeout(async () => {
            await compareAndSyncTagsWithDynamoDB(workspace);
          }, 0);
          
          // NO recargar automáticamente para evitar cerrar modales

        } catch (error) {
          console.error('Error updating task in Lambda:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in updateTask:', error);
      throw error;
    }
  };

  const deleteTask = async (workspace, taskId) => {
    try {
      // Verificar primero si existe la tarea
      const existingTasks = workspaces[workspace]?.tasks || [];
      const taskToDelete = existingTasks.find(t => t.id === taskId);
      
      if (!taskToDelete) {
        console.error(`No se puede eliminar: tarea ${taskId} no encontrada en workspace ${workspace}`);
        return false;
      }
      
      // Registrar quién eliminó la tarea en el historial antes de eliminarla
      const timestamp = generateLocalTimestamp();
      const userEmail = currentUser?.email || 'Usuario';
      
      // Crear entrada para el historial de actividad
      const deleteActivityEntry = {
        timestamp: timestamp,
        type: "TASK_DELETED",
        field: "status",
        oldValue: taskToDelete.status,
        newValue: "deleted",
        updatedBy: userEmail
      };
      
      // Actualizar el historial de actividad de la tarea antes de eliminarla
      const activityHistory = [
        ...(taskToDelete.activityHistory || []), 
        deleteActivityEntry
      ];
      
      // Actualizar la tarea con el historial antes de eliminarla
      try {
        await updateTaskInLambda(taskId, {
          activityHistory: activityHistory,
          updatedAt: timestamp
        });
      } catch (updateError) {
        console.error('Error updating activity history before delete:', updateError);
        // Continuar con la eliminación aunque falle la actualización del historial
      }
      
      // Actualizar estado local primero para mejor respuesta UI
      setWorkspaces(prevWorkspaces => ({
        ...prevWorkspaces,
        [workspace]: {
          ...prevWorkspaces[workspace],
          tasks: prevWorkspaces[workspace].tasks.filter(task => task.id !== taskId)
        }
      }));
      
      // Eliminar de Lambda usando taskId (que ahora es igual a uuid) y registrar quién la eliminó
      try {
        await deleteTaskFromLambda(taskId, userEmail);
        
        // Comparar y sincronizar etiquetas con DynamoDB después de eliminar
        setTimeout(async () => {
          await compareAndSyncTagsWithDynamoDB(workspace);
        }, 0);
        
        return true;
      } catch (error) {
        console.error('Error deleting task from Lambda:', error);
        
        // Si falla, intentar recargar el estado desde Lambda
        try {
          const tasksFromLambda = await fetchTasksFromLambda(workspace, true);
          setWorkspaces(prevWorkspaces => ({
            ...prevWorkspaces,
            [workspace]: {
              ...prevWorkspaces[workspace],
              tasks: tasksFromLambda
            }
          }));
        } catch (refreshError) {
          console.error('Error refreshing tasks after delete error:', refreshError);
        }
        
        return false;
      }
    } catch (error) {
      console.error('Error in deleteTask function:', error);
      return false;
    }
  };

  // Funciones para gestionar workspaces
  const addWorkspace = (name, icon = '') => {
    const id = name.toLowerCase().replace(/\s+/g, '-');

    // Verificar si ya existe un workspace con ese ID
    if (workspaces[id]) {
      return null; // Ya existe
    }

    // Agregar a los datos de workspace
    setWorkspaceData(prev => [...prev, { id, name, icon }]);
    
    // Crear el workspace vacío con arreglo de etiquetas y filtro vacío
    setWorkspaces(prev => ({
      ...prev,
      [id]: {
        tasks: [],
        availableTags: [],
        activeFilter: "",
        activeAssigneeFilter: "",
        activeTitleFilter: ""
      }
    }));

    // Establecer como workspace activo
    setActiveWorkspace(id);

    return id;
  };
  
  const updateWorkspace = async (id, updates) => {
    // Update local state first
    setWorkspaceData(prev => 
      prev.map(workspace => workspace.id === id ? { ...workspace, ...updates } : workspace)
    );
    
    try {
      // Find the workspace to get its UUID
      const workspaceToUpdate = workspaceData.find(workspace => workspace.id === id);
      
      if (workspaceToUpdate) {
        const updatePayload = {
          code: workspaceToUpdate.code || id,
          workspace: updates.name || workspaceToUpdate.name
        };
        if (updates.permisos !== undefined) updatePayload.permisos = updates.permisos;
        await updateWorkspaceInLambda(workspaceToUpdate.uuid || id, updatePayload);
        
        logMessage(`Workspace "${id}" updated successfully in DynamoDB`);
      }
    } catch (error) {
      console.error('Error updating workspace in DynamoDB:', error);
      // You might want to show an error message to the user here
    }
  };
  
  const deleteWorkspace = (id) => {
    // Verificar que no sea el último workspace
    if (workspaceData.length <= 1) {
      return false;
    }
    
    // Actualizar activeWorkspace si es necesario
    if (activeWorkspace === id) {
      const firstOtherWorkspace = workspaceData.find(w => w.id !== id);
      if (firstOtherWorkspace) {
        setActiveWorkspace(firstOtherWorkspace.id);
      }
    }
    
    // Buscar el workspace en workspaceData para obtener el uuid
    const workspaceToDelete = workspaceData.find(workspace => workspace.id === id);
    
    if (!workspaceToDelete || !workspaceToDelete.uuid) {
      console.error('No se encontró el UUID del workspace a eliminar');
      return false;
    }
    
    // Llamar a la API para marcar el workspace como eliminado en DynamoDB
    deleteWorkspaceFromLambda(workspaceToDelete.uuid)
      .then(response => {
        
        // Eliminar de workspaceData
        setWorkspaceData(prev => prev.filter(workspace => workspace.id !== id));
        
        // Eliminar de workspaces
        setWorkspaces(prev => {
          const newWorkspaces = { ...prev };
          delete newWorkspaces[id];
          return newWorkspaces;
        });
      })
      .catch(error => {
        console.error('Error al eliminar workspace en DynamoDB:', error);
      });
    
    return true;
  };

  // Función para asignar un icono predeterminado según el nombre del workspace
  const getDefaultIcon = (workspaceName) => {
    // Ya no usamos emoticonos para los tableros
    return '';
  };

  // Añadir nueva función para administrar etiquetas
  const addTag = async (workspace, tag) => {
    // Convertir la etiqueta a mayúsculas
    const upperCaseTag = tag.toUpperCase();
    
    // Obtener el workspace actual desde workspaceData
    const currentWorkspaceData = workspaceData.find(ws => ws.id === workspace);
    if (!currentWorkspaceData || !currentWorkspaceData.uuid) {
      console.error('No se encontró el workspace o no tiene UUID');
      return false;
    }
    
    // Verificar si la etiqueta ya existe localmente
    if (workspaces[workspace]?.availableTags?.includes(upperCaseTag)) {
      return false;
    }
    
    try {
      // Obtener las etiquetas actuales del workspace
      const currentTags = workspaces[workspace]?.availableTags || [];
      const updatedTags = [...currentTags, upperCaseTag];
      
      // Actualizar el workspace en DynamoDB con las nuevas etiquetas
      await updateWorkspaceInLambda(currentWorkspaceData.uuid, {
        availableTags: updatedTags
      });
      
      // Actualizar estado local solo si la actualización en DynamoDB fue exitosa
      setWorkspaces(prevWorkspaces => ({
        ...prevWorkspaces,
        [workspace]: {
          ...prevWorkspaces[workspace],
          availableTags: updatedTags
        }
      }));
      
      logMessage(`Etiqueta "${upperCaseTag}" añadida al workspace ${workspace}`);
      return true;
    } catch (error) {
      console.error('Error al añadir etiqueta en DynamoDB:', error);
      return false;
    }
  };

  // Nueva función para cargar etiquetas desde DynamoDB
  const loadWorkspaceTagsFromDynamoDB = async (workspace) => {
    try {
      const currentWorkspaceData = workspaceData.find(ws => ws.id === workspace);
      
      if (!currentWorkspaceData) {
        return [];
      }
      
      if (!currentWorkspaceData.code) {
        return [];
      }
      
      // Obtener los workspaces desde DynamoDB para obtener las etiquetas
      const workspacesFromDB = await fetchWorkspacesFromLambda(currentWorkspaceData.code, false, true);
      
      if (!workspacesFromDB || workspacesFromDB.length === 0) {
        return [];
      }
      
      const workspaceFromDB = workspacesFromDB.find(ws => ws.uuid === currentWorkspaceData.uuid);
      
      if (workspaceFromDB && workspaceFromDB.availableTags) {
        return Array.isArray(workspaceFromDB.availableTags) ? workspaceFromDB.availableTags : [];
      }
      
      return [];
    } catch (error) {
      return [];
    }
  };

  // Función para migrar etiquetas existentes del localStorage a DynamoDB
  const migrateTagsToDatabase = useCallback(async () => {
    try {
      logMessage('Iniciando migración de etiquetas a DynamoDB...');
      
      // Recorrer todos los workspaces locales
      for (const workspaceId in workspaces) {
        const localWorkspace = workspaces[workspaceId];
        const workspaceData_item = workspaceData.find(ws => ws.id === workspaceId);
        
        // Solo migrar si hay etiquetas locales y tenemos UUID del workspace
        if (localWorkspace.availableTags && 
            localWorkspace.availableTags.length > 0 && 
            workspaceData_item && 
            workspaceData_item.uuid) {
          
          try {
            // Verificar si ya existen etiquetas en DynamoDB
            const existingTags = await loadWorkspaceTagsFromDynamoDB(workspaceId);
            
            if (existingTags.length === 0) {
              // Solo migrar si no hay etiquetas en DynamoDB
              await updateWorkspaceInLambda(workspaceData_item.uuid, {
                availableTags: localWorkspace.availableTags
              });
              
              logMessage(`Etiquetas migradas para workspace ${workspaceId}:`, localWorkspace.availableTags);
            } else {
              logMessage(`Workspace ${workspaceId} ya tiene etiquetas en DynamoDB, omitiendo migración`);
            }
          } catch (error) {
            console.error(`Error al migrar etiquetas para workspace ${workspaceId}:`, error);
          }
        }
      }
      
      logMessage('Migración de etiquetas completada');
    } catch (error) {
      console.error('Error durante la migración de etiquetas:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces, workspaceData]);

  // Función para actualizar el filtro de un workspace
  const updateWorkspaceFilter = (workspace, filter) => {
    setWorkspaces(prevWorkspaces => ({
      ...prevWorkspaces,
      [workspace]: {
        ...prevWorkspaces[workspace],
        activeFilter: filter
      }
    }));
  };

  // Función para actualizar el filtro de usuario asignado
  const updateAssigneeFilter = (workspace, assignee) => {
    setWorkspaces(prevWorkspaces => ({
      ...prevWorkspaces,
      [workspace]: {
        ...prevWorkspaces[workspace],
        activeAssigneeFilter: assignee
      }
    }));
  };

  // Función para actualizar el filtro de título de tarea
  const updateTitleFilter = (workspace, title) => {
    setWorkspaces(prevWorkspaces => ({
      ...prevWorkspaces,
      [workspace]: {
        ...prevWorkspaces[workspace],
        activeTitleFilter: title
      }
    }));
  };

  // Función para obtener usuarios asignados únicos en un workspace
  const getUniqueAssignees = (workspace) => {
    if (!workspaces[workspace]?.tasks) return ["Usuario"];
    
    // Extraer todos los usuarios asignados únicos que están en las tarjetas
    const assignees = new Set();
    
    // Asegurarnos de que siempre incluya "Usuario" como opción predeterminada
    assignees.add("Usuario");
    
    workspaces[workspace].tasks.forEach(task => {
      if (task.assignee && task.assignee !== "Usuario") {
        assignees.add(task.assignee);
      }
    });
    
    return Array.from(assignees).sort();
  };

  // Función para obtener etiquetas únicas de las tarjetas en un workspace
  const getUniqueTags = useCallback((workspace) => {
    if (!workspaces[workspace]?.tasks) return [];
    
    // Extraer todas las etiquetas únicas que se usan actualmente en las tarjetas
    const tags = new Set();
    
    workspaces[workspace].tasks.forEach(task => {
      if (task.tags && Array.isArray(task.tags)) {
        task.tags.forEach(tag => {
          // Procesar cada etiqueta para eliminar comas
          if (tag && typeof tag === 'string') {
            // Convertir a mayúsculas y eliminar espacios en blanco
            const cleanTag = tag.trim().toUpperCase();
            // Reemplazar comas por espacios o eliminarlas
            const finalTag = cleanTag.replace(/,/g, '');
            if (finalTag) {
              tags.add(finalTag);
            }
          }
        });
      }
    });
    
    // Ya no combinamos con las etiquetas predefinidas para mostrar solo las que están en uso
    return Array.from(tags).sort();
  }, [workspaces]);

  // Función para sincronizar availableTags con las etiquetas de las tareas existentes
  const syncWorkspaceTags = async (workspace) => {
    if (!workspaces[workspace]) return;
    
    // Obtener etiquetas únicas de las tareas existentes
    const uniqueTags = getUniqueTags(workspace);
    
    // Actualizar availableTags del workspace localmente
    setWorkspaces(prevWorkspaces => ({
      ...prevWorkspaces,
      [workspace]: {
        ...prevWorkspaces[workspace],
        availableTags: uniqueTags
      }
    }));
    
    logMessage(`Etiquetas sincronizadas para workspace '${workspace}': ${uniqueTags.join(', ')}`);
    
    // Nota: No persistimos availableTags en DynamoDB porque se generan dinámicamente
    // desde las tareas existentes. Esto asegura que siempre estén sincronizadas.
  };

  // Función para forzar la sincronización de etiquetas en todos los workspaces
  const syncAllWorkspaceTags = async () => {
    const workspaceIds = Object.keys(workspaces);
    
    for (const workspaceId of workspaceIds) {
      await syncWorkspaceTags(workspaceId);
    }
    
    logMessage(`Sincronización de etiquetas completada para ${workspaceIds.length} workspaces`);
  };

  // Función para añadir comentario a una tarea
  const addComment = async (workspace, taskId, commentData) => {
    // Find the task
    const task = workspaces[workspace]?.tasks.find(task => task.id === taskId);
    
    if (!task) {
      console.error(`Task ${taskId} not found in workspace ${workspace}`);
      return;
    }
    
    const timestamp = new Date().toISOString();
    const userEmail = currentUser?.email || 'Usuario';
    
    // Handle both string and object comment data
    let newComment;
    if (typeof commentData === 'string') {
      // Legacy string format
      newComment = {
        id: `comment-${Date.now()}`,
        text: commentData,
        timestamp: timestamp,
        author: userEmail,
        authorId: currentUser?.uid
      };
    } else {
      // New object format with more data
      newComment = {
        id: `comment-${Date.now()}`,
        ...commentData,
        timestamp: commentData.timestamp || timestamp,
        author: commentData.author || userEmail,
        authorId: commentData.authorId || currentUser?.uid
      };
    }
    
    // Add the comment to the existing comments array
    const updatedComments = [...(task.comments || []), newComment];
    
    // Create activity entry for the comment
    const commentActivityEntry = {
      timestamp: timestamp,
      type: "COMMENT_ADDED",
      field: "comments",
      oldValue: null,
      newValue: typeof commentData === 'string' ? commentData : commentData.text,
      updatedBy: userEmail
    };
    
    // Update the activity history
    const updatedActivityHistory = [...(task.activityHistory || []), commentActivityEntry];
    
    // Prepare the updates object
    const updates = {
      comments: updatedComments,
      activityHistory: updatedActivityHistory,
      updatedAt: timestamp
    };
    
    // Update local state first
    setWorkspaces(prevWorkspaces => ({
      ...prevWorkspaces,
      [workspace]: {
        ...prevWorkspaces[workspace],
        tasks: prevWorkspaces[workspace].tasks.map(task => 
          task.id === taskId ? { 
            ...task, 
            ...updates 
          } : task
        )
      }
    }));
    
    try {
      // Call Lambda to update the task (usando taskId que ahora es igual a uuid)
      await updateTaskInLambda(taskId, updates);
    } catch (error) {
      console.error('Error adding comment in Lambda:', error);
    }
  };

  // Función para eliminar un comentario
  const deleteComment = async (workspace, taskId, commentId) => {
    // Find the task
    const task = workspaces[workspace]?.tasks.find(task => task.id === taskId);
    
    if (!task) {
      console.error(`Task ${taskId} not found in workspace ${workspace}`);
      return;
    }
    
    // Encontrar el comentario a eliminar
    const commentToDelete = task.comments?.find(comment => comment.id === commentId);
    
    if (!commentToDelete) {
      console.error(`Comment ${commentId} not found in task ${taskId}`);
      return;
    }
    
    const timestamp = new Date().toISOString();
    const userEmail = currentUser?.email || 'Usuario';
    
    // Filtrar el comentario del array de comentarios
    const updatedComments = (task.comments || []).filter(comment => comment.id !== commentId);
    
    // Si el comentario tiene archivos adjuntos, también eliminarlos de la lista de archivos de la tarea
    let updatedFiles = task.files || [];
    let fileActivityEntry = null;
    
    if (commentToDelete.attachments && commentToDelete.attachments.length > 0) {
      const filesToRemove = commentToDelete.attachments.map(att => att.name);
      const oldFiles = [...updatedFiles];
      updatedFiles = updatedFiles.filter(file => !filesToRemove.includes(file));
      
      // Crear entrada en el historial de actividad para los archivos eliminados
      if (oldFiles.length !== updatedFiles.length) {
        fileActivityEntry = {
          timestamp: timestamp,
          type: "ATTACHMENT_DELETED",
          field: "files",
          oldValue: oldFiles.join(', '),
          newValue: updatedFiles.join(', '),
          updatedBy: userEmail
        };
      }
    }
    
    // Crear entrada en el historial de actividad para el comentario
    const commentActivityEntry = {
      timestamp: timestamp,
      type: "COMMENT_DELETED",
      field: "comments",
      oldValue: commentToDelete.text || null,
      newValue: null,
      updatedBy: userEmail
    };
    
    // Actualizar el historial de actividad
    const updatedActivityHistory = [
      ...(task.activityHistory || []), 
      commentActivityEntry,
      ...(fileActivityEntry ? [fileActivityEntry] : [])
    ];
    
    // Preparar las actualizaciones
    const updates = {
      comments: updatedComments,
      files: updatedFiles,
      activityHistory: updatedActivityHistory,
      updatedAt: timestamp
    };
    
    // Update local state first
    setWorkspaces(prevWorkspaces => ({
      ...prevWorkspaces,
      [workspace]: {
        ...prevWorkspaces[workspace],
        tasks: prevWorkspaces[workspace].tasks.map(task => 
          task.id === taskId ? { 
            ...task, 
            ...updates 
          } : task
        )
      }
    }));
    
    try {
      // Call Lambda to update the task (usando taskId que ahora es igual a uuid)
      await updateTaskInLambda(taskId, updates);
    } catch (error) {
      console.error('Error deleting comment in Lambda:', error);
    }
  };

  // Función para editar un comentario
  const editComment = async (workspace, taskId, commentId, newText) => {
    // Find the task
    const task = workspaces[workspace]?.tasks.find(task => task.id === taskId);
    
    if (!task) {
      console.error(`Task ${taskId} not found in workspace ${workspace}`);
      return;
    }
    
    // Encontrar el comentario a editar
    const commentToEdit = task.comments?.find(comment => comment.id === commentId);
    
    if (!commentToEdit) {
      console.error(`Comment ${commentId} not found in task ${taskId}`);
      return;
    }
    
    const timestamp = new Date().toISOString();
    const userEmail = currentUser?.email || 'Usuario';
    
    // Actualizar el comentario en el array de comentarios
    const updatedComments = (task.comments || []).map(comment => 
      comment.id === commentId 
        ? { ...comment, text: newText, editedAt: timestamp }
        : comment
    );
    
    // Crear entrada en el historial de actividad
    const commentActivityEntry = {
      timestamp: timestamp,
      type: "COMMENT_EDITED",
      field: "comments",
      oldValue: commentToEdit.text || null,
      newValue: newText,
      updatedBy: userEmail
    };
    
    // Actualizar el historial de actividad
    const updatedActivityHistory = [...(task.activityHistory || []), commentActivityEntry];
    
    // Preparar las actualizaciones
    const updates = {
      comments: updatedComments,
      activityHistory: updatedActivityHistory,
      updatedAt: timestamp
    };
    
    // Update local state first
    setWorkspaces(prevWorkspaces => ({
      ...prevWorkspaces,
      [workspace]: {
        ...prevWorkspaces[workspace],
        tasks: prevWorkspaces[workspace].tasks.map(task => 
          task.id === taskId ? { ...task, ...updates } : task
        )
      }
    }));
    
    // Update in Lambda
    try {
      await updateTaskInLambda(taskId, updates);
    } catch (error) {
      console.error('Error editing comment in Lambda:', error);
    }
  };

  // Function to refresh tasks for a workspace
  const refreshTasks = async (workspace = activeWorkspace) => {
    if (!workspace) return;
    
    try {
      setWorkspacesLoading(true);
      logMessage(`Refrescando datos para workspace '${workspace}'...`);
      
      // Get department code
      const departmentCodeMatch = userData?.department?.match(/^(\d+)/);
      const userDepartmentCode = departmentCodeMatch ? departmentCodeMatch[1] : userData?.department;
      
      // Load updated permissions and workspaces
      await refreshData(workspace, userDepartmentCode);
      
      // Force reload of workspaces with latest permissions
      await loadWorkspaces(userDepartmentCode, true);
      
      logMessage(`Datos refrescados para workspace '${workspace}'`);
    } catch (error) {
      console.error(`Error al refrescar datos para workspace '${workspace}':`, error);
    } finally {
      setWorkspacesLoading(false);
    }
  };

  // Function to manually reload tasks without affecting UI state
  const reloadTasksFromServer = useCallback(async (workspace = activeWorkspace) => {
    if (!workspace) return;
    
    try {
  
      const tasksFromLambda = await fetchTasksFromLambda(workspace, true);
      
      // Procesar tareas para archivado automático
      const tasksToArchive = [];
      const processedTasks = tasksFromLambda.map(task => {
        if (shouldTaskBeArchived(task)) {
          tasksToArchive.push(task);
          return {
            ...task,
            ArchivedDate: true
          };
        }
        return task;
      });
      
      // Si hay tareas que necesitan ser archivadas, actualizarlas en el backend
      if (tasksToArchive.length > 0) {

        
        // Actualizar cada tarea archivada en el backend
        for (const task of tasksToArchive) {
          try {
            await updateTaskInLambda(task.id, { ArchivedDate: true });

          } catch (error) {
            console.error(`Error al archivar automáticamente la tarea ${task.id}:`, error);
          }
        }
      }
      
      // Filtrar las tareas archivadas para no mostrarlas en el frontend
      const visibleTasks = filterArchivedTasks(processedTasks);
      
      // Actualizar solo las tareas sin afectar otros estados
      setWorkspaces(prevWorkspaces => ({
        ...prevWorkspaces,
        [workspace]: {
          ...prevWorkspaces[workspace],
          tasks: visibleTasks
        }
      }));
      
      // Sincronizar las etiquetas disponibles con las únicas de las tareas
      syncWorkspaceTags(workspace);
      
      return visibleTasks;
    } catch (error) {
      console.error(`Error al recargar tareas desde el servidor para workspace '${workspace}':`, error);
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace]);

  // Function to find workspaces where user has created tasks
  const findWorkspacesWithUserTasks = async (userEmail) => {
    try {
      // Esta función necesitará hacer una búsqueda en todas las workspaces para encontrar tareas del usuario
      // Por ahora implementaré una versión básica que busca en workspaces conocidos
      const workspacesWithTasks = [];
      
      // Obtener todos los workspaces disponibles en modo admin para buscar tareas del usuario
      const allWorkspaces = await fetchWorkspacesFromLambda('', true, true);
      
      if (allWorkspaces && allWorkspaces.length > 0) {
        // Para cada workspace, verificar si el usuario tiene tareas
        for (const ws of allWorkspaces) {
          try {
            const tasks = await fetchTasksFromLambda(ws.workspace.toLowerCase().replace(/\s+/g, '-'));
            
            // Verificar si hay tareas creadas por este usuario
            const userTasks = tasks.filter(task => 
              task.createdBy === userEmail
            );
            
            if (userTasks.length > 0) {
              workspacesWithTasks.push({
                ...ws,
                isUserTasksOnly: true, // Marcar que este workspace es solo para ver tareas del usuario
                userTaskCount: userTasks.length
              });
            }
          } catch (error) {
            console.error(`Error checking tasks in workspace ${ws.workspace}:`, error);
          }
        }
      }
      
      return workspacesWithTasks;
    } catch (error) {
      console.error('Error finding workspaces with user tasks:', error);
      return [];
    }
  };

  // Function to get all available workspaces (for users with sendTasks permission)
  const getAllAvailableWorkspaces = async () => {
    try {
      logMessage('Obteniendo todos los workspaces disponibles...');
      
      // Obtener todos los workspaces en modo admin
      const allWorkspaces = await fetchWorkspacesFromLambda('', true, true);
      
      if (allWorkspaces && allWorkspaces.length > 0) {
        // Convertir al formato esperado por la interfaz
        const formattedWorkspaces = allWorkspaces.map(ws => ({
          id: ws.workspace.toLowerCase().replace(/\s+/g, '-'),
          name: ws.workspace,
          icon: getDefaultIcon(ws.workspace),
          uuid: ws.uuid,
          code: ws.code // Incluir el código del workspace
        }));
        
        logMessage(`Se encontraron ${formattedWorkspaces.length} workspaces totales`);
        return formattedWorkspaces;
      }
      
      return [];
    } catch (error) {
      console.error('Error getting all available workspaces:', error);
      return [];
    }
  };

  // Function to manually run archiving process for a workspace
  const runManualArchiving = async (workspace = activeWorkspace) => {
    if (!workspace) {
      throw new Error('No hay workspace activo para ejecutar el archivado');
    }

    try {

      
      const currentTasks = workspaces[workspace]?.tasks || [];
      const stats = getArchivingStats(currentTasks);
      
      
      
      if (stats.pendingArchival === 0) {
        
        return {
          success: true,
          archivedCount: 0,
          message: 'No hay tareas pendientes de archivado'
        };
      }

      // Archivar cada tarea que cumple los criterios
      let archivedCount = 0;
      const errors = [];

      for (const task of stats.tasksToArchive) {
        try {
          await updateTaskInLambda(task.id, { ArchivedDate: true });
          archivedCount++;
          
        } catch (error) {
          console.error(`Error al archivar manualmente la tarea ${task.id}:`, error);
          errors.push({ taskId: task.id, error: error.message });
        }
      }

      // Recargar las tareas para reflejar los cambios
      await reloadTasksFromServer(workspace);

      const result = {
        success: true,
        archivedCount: archivedCount,
        totalPending: stats.pendingArchival,
        errors: errors,
        message: `Se archivaron ${archivedCount} de ${stats.pendingArchival} tareas pendientes`
      };

      
      return result;
    } catch (error) {
      console.error('Error en el archivado manual:', error);
      throw error;
    }
  };

  // Function to get archiving statistics for current workspace
  const getWorkspaceArchivingStats = (workspace = activeWorkspace) => {
    if (!workspace || !workspaces[workspace]) {
      return {
        total: 0,
        completed: 0,
        archived: 0,
        pendingArchival: 0,
        tasksToArchive: []
      };
    }

    const currentTasks = workspaces[workspace].tasks || [];
    return getArchivingStats(currentTasks);
  };

  // Función para comparar y sincronizar etiquetas con DynamoDB
  const compareAndSyncTagsWithDynamoDB = useCallback(async (workspace) => {
    if (!workspaces[workspace]) return;
    
    // Obtener etiquetas únicas de las tareas existentes (las que aparecen en el filtro)
    const filterTags = getUniqueTags(workspace);
    
    // Obtener las etiquetas actuales de DynamoDB
    const workspaceInfo = workspaceData.find(ws => ws.id === workspace);
    if (!workspaceInfo) return;
    
    const dynamoTags = workspaceInfo.availableTags || [];
    
    // Comparar las etiquetas
    // eslint-disable-next-line no-unused-vars
    const filterTagsSet = new Set(filterTags);
    const dynamoTagsSet = new Set(dynamoTags);
    
    // Encontrar etiquetas que están en el filtro pero no en DynamoDB
    const missingTags = filterTags.filter(tag => !dynamoTagsSet.has(tag));
    
    // Si hay etiquetas faltantes, actualizar DynamoDB
    if (missingTags.length > 0) {
      try {
        // Crear la lista completa de etiquetas (las existentes + las faltantes)
        const updatedTags = [...new Set([...dynamoTags, ...missingTags])];
        
        logMessage(`Sincronizando etiquetas para workspace '${workspace}': añadiendo ${missingTags.join(', ')}`);
        
        // Actualizar el workspace en DynamoDB
        await updateWorkspaceInLambda(workspaceInfo.uuid, {
          availableTags: updatedTags
        });
        
        // Actualizar el workspaceData local para reflejar el cambio
        setWorkspaceData(prevData => 
          prevData.map(ws => 
            ws.id === workspace 
              ? { ...ws, availableTags: updatedTags }
              : ws
          )
        );
        
        // Actualizar el estado local del workspace
        setWorkspaces(prevWorkspaces => ({
          ...prevWorkspaces,
          [workspace]: {
            ...prevWorkspaces[workspace],
            availableTags: updatedTags
          }
        }));
        
        logMessage(`Etiquetas actualizadas en DynamoDB para workspace '${workspace}': ${updatedTags.join(', ')}`);
        
      } catch (error) {
        console.error('Error actualizando etiquetas en DynamoDB:', error);
        logMessage(`Error actualizando etiquetas en DynamoDB para workspace '${workspace}': ${error.message}`);
      }
    } else {
      logMessage(`Etiquetas ya sincronizadas para workspace '${workspace}'`);
    }
  }, [workspaces, workspaceData, getUniqueTags]);

  const value = {
    workspaces,
    activeWorkspace,
    setActiveWorkspace,
    workspaceData,
    addTask,
    getUserPermissions,
    getPermissionForBoard,
    canUserDoOnBoard,
    addWorkspace,
    updateWorkspace,
    deleteWorkspace,
    userAccessibleWorkspaces,
    workspacesLoading,
    addTag,
    loadWorkspaceTagsFromDynamoDB,
    updateWorkspaceFilter,
    updateAssigneeFilter,
    updateTitleFilter,
    getUniqueAssignees,
    getUniqueTags,
    addComment,
    deleteComment,
    editComment,
    refreshTasks,
    reloadTasksFromServer,
    updateTaskStatus,
    updateTask,
    deleteTask,
    findWorkspacesWithUserTasks,
    getAllAvailableWorkspaces,
    runManualArchiving,
    getWorkspaceArchivingStats,
    syncAllWorkspaceTags, // Exponer la nueva función para sincronizar todas las etiquetas
    compareAndSyncTagsWithDynamoDB // Exponer la nueva función para comparar y sincronizar etiquetas
  };

  return (
    <KanbanContext.Provider
      value={value}
    >
      {children}
    </KanbanContext.Provider>
  );
} 